import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { configs } from '@/configs';
import { network } from '@/configs/network';
import useBalance from '@/hooks/useBalance';
import { TokenDenom } from '@/hooks/useTokenPrices';
import type { InputExecuteSSO } from '@/libs/ssoWallet';
import { ActionSSO, ssoExecute } from '@/libs/ssoWallet';
import { useRouter } from '@/provider/RouterProvider';
import { useAuth } from '@/provider/UserProvider';
import type { Prompt } from '@/types/prompt';
import { getMessageExecuteBuyingTransaction721WithCw20Token } from '@/utils/transactionMessage';
import { handleExecute } from '@/utils/wasm';

const useCheckout = (price: string, onClose: () => void, prompt?: Prompt) => {
  const { usd } = useBalance([TokenDenom.USDT]);
  const router = useRouter();
  const { auth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [finish, setFinish] = useState('');
  const isEnough = parseFloat(price) <= usd;

  const handleExecuteWithOwallet = async (
    contract: string,
    sender: string,
    message: string,
    additionalInfor: any,
  ) => {
    setLoading(true);
    const purchaseResult = await handleExecute(contract, sender, message, additionalInfor);
    setLoading(false);

    if (purchaseResult.tx_response.txhash) {
      setFinish('Prompt purchased successfully');
      onClose();
    }
  };

  const handleExecuteWithSSO = async (contract: string, message: string) => {
    setLoading(true);
    const input: InputExecuteSSO = {
      contractAddress: contract,
      chainId: network?.chainId || 'Oraichain',
      params: message,
    };
    await ssoExecute(input, ActionSSO.buyPrompt);
  };

  const onPurchase = async () => {
    try {
      // guard code, prevent crash
      if (!prompt) return;

      const offering = prompt.asset.offer;
      if (!offering) return;
      const sender = auth.user.publicAddress;
      const marketplace721 =
        offering?.version === 2 ? configs.marketPlace721Contract : configs.marketPlaceContract;
      const offering_id = offering.offeringId;
      const contract = configs.usdtTokenContract || '';
      const message = getMessageExecuteBuyingTransaction721WithCw20Token(
        offering_id,
        price,
        marketplace721 || '',
      );
      const additionalInfor = {
        gas: 4000000,
        type: 'cw20',
        fees: 0,
      };

      if (auth.isOwallet) {
        await handleExecuteWithOwallet(contract, sender, message, additionalInfor);
      } else {
        await handleExecuteWithSSO(contract, message);
      }
    } catch (error: any) {
      setLoading(false);

      if (error === 'popup-closed') {
        toast.error(
          'The signing transaction popup might be closed or your browser has blocked the popup! Please try again or check your browser setting',
        );
        return;
      }
      toast.error(error?.message);
    }
  };

  // for sso
  useEffect(() => {
    if (typeof window !== 'undefined' && !auth.isOwallet) {
      window.addEventListener(
        'message',
        (event) => {
          if (event.origin !== configs.ssoUrl) return;
          const data = JSON.parse(event?.data || '');

          if (data.action !== ActionSSO.buyPrompt) return;
          setLoading(false);

          if (data.status === 'success') {
            onClose();
            setFinish('Prompt purchased successfully');
          } else {
            setErr(data.response);
          }
        },
        false,
      );
    }
    return window.removeEventListener('message', () => {});
  }, []);

  useEffect(() => {
    if (err) {
      toast.error(err);
    }
  }, [err]);

  useEffect(() => {
    if (finish && prompt) {
      toast.success(finish);
      router.push(`/profile/${auth.user.publicAddress}/prompt/${prompt.id}`);
    }
  }, [finish, prompt]);

  return { isEnough, onPurchase, loading };
};

export default useCheckout;
